import { useQueryClient } from '@tanstack/react-query';
import { createPlatformCompany } from 'api/platformCompanies';
import { platformCompaniesKeys } from 'api/queryKeys';
import { Box, Flex, Line } from 'components';
import { BackButton } from 'components/BackButton';
import { Button, TextField } from 'components/_form';
import { H1, H2 } from 'components/Typography/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IPlatformCompanyForm } from 'types/platformCompany';
import { Wrapper } from '../../CompanyAdministrationView/CompanyAdministrationView.styled';

const emptyCompanyForm: IPlatformCompanyForm = {
  full_name: '',
  short_name: '',
  user_code: '',
  email_domain: '',
  default_language_code: 'pl',
  email: '',
  phone: '',
  service_description: ''
};

export const PlatformCompanyCreateView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [saving, setSaving] = useState(false);
  const [companyForm, setCompanyForm] = useState<IPlatformCompanyForm>(emptyCompanyForm);

  const handleCompanyChange = (field: keyof IPlatformCompanyForm, value: string) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!companyForm.full_name || !companyForm.short_name) {
      toast.error(t('platformCompanies.validation.companyRequired'));
      return;
    }

    setSaving(true);
    try {
      await createPlatformCompany({ company: companyForm });
      await queryClient.invalidateQueries({ queryKey: platformCompaniesKeys.all() });
      toast.success(t('common.success'));
      navigate('/app/platform/companies');
    } catch (err: any) {
      toast.error(err?.message || t('anErrorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper mb={94} flexDirection="column">
      <Flex width="100%" alignItems="center">
        <BackButton />
        <H1 m={2} variant={['h3', 'h2']} textTransform="uppercase">
          {t('platform.title')} | {t('platform.companies.title')} |{' '}
          {t('platformCompanies.addCompany')}
        </H1>
      </Flex>
      <Line />

      <Box mt={3}>
        <H2 variant="h3">{t('platformCompanies.companySection')}</H2>
        <Flex flexWrap="wrap" gap="16px" mt={2}>
          <TextField
            label="platformCompanies.fields.fullName"
            value={companyForm.full_name}
            onChange={(e) => handleCompanyChange('full_name', e.target.value)}
          />
          <TextField
            label="platformCompanies.fields.shortName"
            value={companyForm.short_name}
            onChange={(e) => handleCompanyChange('short_name', e.target.value)}
          />
          <TextField
            label="platformCompanies.fields.userCode"
            value={companyForm.user_code}
            onChange={(e) => handleCompanyChange('user_code', e.target.value)}
          />
          <TextField
            label="platformCompanies.fields.emailDomain"
            value={companyForm.email_domain}
            onChange={(e) => handleCompanyChange('email_domain', e.target.value)}
          />
          <TextField
            label="platformCompanies.fields.defaultLanguage"
            value={companyForm.default_language_code}
            onChange={(e) => handleCompanyChange('default_language_code', e.target.value)}
          />
          <TextField
            label="platformCompanies.fields.email"
            value={companyForm.email}
            onChange={(e) => handleCompanyChange('email', e.target.value)}
          />
          <TextField
            label="platformCompanies.fields.phone"
            value={companyForm.phone}
            onChange={(e) => handleCompanyChange('phone', e.target.value)}
          />
          <TextField
            label="platformCompanies.fields.serviceDescription"
            value={companyForm.service_description}
            onChange={(e) => handleCompanyChange('service_description', e.target.value)}
          />
        </Flex>
      </Box>

      <Flex mt={4} gap="12px">
        <Button variant="eucalyptus" minWidth="160px" onClick={handleSave} disabled={saving}>
          {t('common.save')}
        </Button>
      </Flex>
    </Wrapper>
  );
};
